
import { ThemeEvents } from '@theme/events';

class CartItemsHelper {
  constructor() {
    this.cartEl = document.querySelector('cart-items-component');
    if (!this.cartEl) {
      console.log('CartItemsHelper: cart-items-component not found');
      return;
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate.bind(this));
    this.handleOnloadCart();

  }

  addFreeGift(freePromoProduct) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionId = this.cartEl.dataset.sectionId;
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    if (freePromoProduct) {
      // Perf #1: bundled section rendering — /cart/add.js with `sections` returns the rendered cart
      // section(s) in the same call (validated against shopify.dev), instead of refetching + parsing the
      // whole page. Mirrors addVariant / the reco add path.
      const sectionIds = Array.from(sectionsToUpdate);
      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: freePromoProduct,
          quantity: 1,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      })
      .then(res => res.json())
      .then(added => {
        if (added && added.status) { console.error('Add free gift failed:', added.description); return; }
        const rendered = added.sections || {};

        sectionIds.forEach(sectionId => {
          const html = rendered[sectionId];
          if (!html) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const drawerNew =
            doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
            doc.querySelector('cart-items-component');
          const drawerOld = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);

          if (!drawerNew || !drawerOld) return;

          const freePromoProductInCartKey = drawerNew.querySelector('free-shipping-component.promo-progress-bar-ext')?.getAttribute('free-promo-product-key');
          const freePromoItem = drawerNew.querySelector(`.cart-items__table-row[data-key="${freePromoProductInCartKey}"]`);
          freePromoItem?.querySelector('.quantity-selector')?.classList.add('hide-this');
          freePromoItem?.querySelector('.cart-items__remove')?.classList.add('hide-this');

          drawerOld.innerHTML = drawerNew.innerHTML;
        });
      })
      .catch(err => console.error('Error adding free gift:', err));
    }

  }

  removeFreeGift(freePromoProductInCartKey) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionId = this.cartEl.dataset.sectionId;
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    if (freePromoProductInCartKey) {
      // Perf #1: bundled section rendering — /cart/change.js with `sections` returns the rendered cart
      // section(s) in the same call (validated against shopify.dev), instead of refetching the whole page.
      const sectionIds = Array.from(sectionsToUpdate);
      fetch(`${Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: freePromoProductInCartKey,
          quantity: 0,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname
        })
      })
        .then(res => res.json())
        .then(changed => {
          const rendered = (changed && changed.sections) || {};
          sectionIds.forEach(sectionId => {
            const html = rendered[sectionId];
            if (!html) return;

            const doc = new DOMParser().parseFromString(html, 'text/html');
            const drawerNew =
              doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
              doc.querySelector('cart-items-component');
            const drawerOld = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);

            if (drawerNew && drawerOld) {
              drawerOld.innerHTML = drawerNew.innerHTML;
            }
          });
        })
        .catch(err => console.error('Error removing free gift:', err));
    }
  }

  /**
   * Add any variant to the cart and refresh the drawer/icon/progress bar, then open the drawer.
   * Reliable, theme-native path (mirrors addFreeGift) — used by the size calculator's
   * "add my size to cart" so it doesn't depend on the PDP buy-buttons being in sync.
   * Returns a promise that rejects if the add fails (e.g. sold out).
   */
  addVariant(variantId, options) {
    options = options || {};
    if (!variantId) return Promise.reject(new Error('no variant id'));

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId && sectionIds.indexOf(item.dataset.sectionId) === -1) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    // Single request: /cart/add.js with `sections` returns the rendered cart section(s) — no full-page fetch.
    const addBody = {
      id: variantId,
      quantity: 1,
      sections: sectionIds.join(','),
      sections_url: window.location.pathname
    };
    // Optional line-item properties (e.g. the size calculator tags its add with `_size_calc`).
    // `_`-prefixed keys are hidden from the customer at checkout but persist on the order.
    if (options.properties && typeof options.properties === 'object') {
      addBody.properties = options.properties;
    }
    return fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(addBody)
    })
      .then((res) => res.json())
      .then((added) => {
        if (added && added.status) throw new Error(added.description || 'Add to cart failed');
        const rendered = added.sections || {};
        sectionIds.forEach((sectionId) => {
          const html = rendered[sectionId];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh =
            doc.querySelector(`cart-items-component[data-section-id="${sectionId}"]`) ||
            doc.querySelector('cart-items-component');
          const old = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
          if (fresh && old) old.innerHTML = fresh.innerHTML;
        });
      })
      .then(() => {
        try { this.handleCartUpdate(); } catch (e) { /* refresh listeners are best-effort */ }
        if (window.shippingService?.getShippingRates) window.shippingService.getShippingRates();
        if (options.openDrawer !== false) document.querySelector('cart-drawer-component')?.open();
      });
  }

  handleCartUpdate(event) {
    const progressBar = document.querySelector('free-shipping-component.promo-progress-bar-ext');
    if (window.shippingService?.getShippingRates) {
      window.shippingService.getShippingRates();
    }

    setTimeout(() => {
      if (progressBar) {
        
        const subTotal = Number(progressBar.getAttribute('cart-items-subtotal'));
        const thresholdGoal = Number(progressBar.getAttribute('threshold-goal'));
        const freePromoProduct = Number(progressBar.getAttribute('free-promo-product'));
        const freePromoProductInCart = progressBar.getAttribute('free-product-in-cart') == 'true';
        const cartItemsIdStr = progressBar.getAttribute('cart-items-id');
        const cartItemsId = cartItemsIdStr.split(',').filter(Boolean).map(Number);

        // The gift step is active only when a gift product is set AND a goal threshold exists.
        // When the gift is sold out, Liquid drops the step and emits threshold-goal="0",
        // so giftActive becomes false and we won't try to add an unavailable gift.
        const giftActive = freePromoProduct && thresholdGoal > 0;

        if (giftActive && subTotal >= thresholdGoal) {
          if (!freePromoProductInCart) {
            this.addFreeGift(freePromoProduct);
          } else {
            this.hideItemAction(progressBar);
          }
        } else {
          // Below the goal, OR the gift step is inactive (sold out / not configured):
          // remove any stale free gift so it can't linger in the cart.
          if (freePromoProductInCart) {
            const freePromoProductInCartKey = progressBar.getAttribute('free-promo-product-key');
            const freePromoItem = document.querySelector(`.cart-items__table-row[data-key="${freePromoProductInCartKey}"]`);
            freePromoItem?.classList.add('hide-this');
            this.removeFreeGift(freePromoProductInCartKey);
          }
        }

      }
    }, 10);

  }

  handleOnloadCart(){
    const progressBar = document.querySelector('free-shipping-component.promo-progress-bar-ext');
    this.hideItemAction(progressBar);
  }

  hideItemAction(progressBar){
    const freePromoProductInCartKey = progressBar?.getAttribute('free-promo-product-key');
    const freePromoItem = document.querySelector(`.cart-items__table-row[data-key="${freePromoProductInCartKey}"]`);
    freePromoItem?.querySelector('.quantity-selector')?.classList.add('hide-this');
    freePromoItem?.querySelector('.cart-items__remove')?.classList.add('hide-this');
  }


}

window.CartItemsHelper = new CartItemsHelper();
